import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save,
  Settings, GripVertical, RotateCcw, ChevronRight, Pencil, Copy,
  Eye, X, Send, Play, MessageSquare, Image, Users, LogOut, FileText, CalendarDays, PenLine
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from "../components/ui/dialog";
import AdminImagesTab from "./AdminImagesTab";
import AdminUsersTab from "./AdminUsersTab";
import AdminLeadsTab from "./AdminLeadsTab";
import AdminCalendarTab from "./AdminCalendarTab";
import AdminBlogTab from "./AdminBlogTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ===================== CHATBOT PREVIEW =====================

function ChatbotPreview({ category, onClose }) {
  const [messages, setMessages] = useState([]);
  const [opts, setOpts] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [done, setDone] = useState(false);
  const endRef = useRef(null);
  const didInit = useRef(false);
  const s = useRef(null);
  if (!s.current) {
    s.current = { questions: [], qIdx: 0, answers: {}, repeatCounts: {}, repeatIter: {}, groupRefs: {} };
  }
  const st = s.current;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, opts]);

  const getCondAnswer = (qId, curQ) => {
    if (curQ.repeat_group) {
      const ref = st.questions.find(q => q.id === qId);
      if (ref && ref.repeat_group === curQ.repeat_group) return st.answers[`${qId}_${st.repeatIter[curQ.repeat_group] || 1}`];
    }
    return st.answers[qId];
  };
  const checkCond = (q) => {
    if (!q.condition) return true;
    const ans = getCondAnswer(q.condition.question_id, q);
    if (ans == null) return false;
    return q.condition.match_values.some(v => q.condition.case_insensitive !== false ? v.toLowerCase() === String(ans).toLowerCase() : v === String(ans));
  };
  const findNext = (start) => {
    let i = start;
    while (i < st.questions.length) {
      const q = st.questions[i];
      if (q.repeat_group) {
        const cnt = st.repeatCounts[q.repeat_group] || 0, it = st.repeatIter[q.repeat_group] || 1;
        if (cnt === 0 || it > cnt) { while (i < st.questions.length && st.questions[i].repeat_group === q.repeat_group) i++; continue; }
      }
      if (!checkCond(q)) { i++; continue; }
      return i;
    }
    return -1;
  };
  const interp = (text, q) => {
    let r = text;
    if (q.repeat_group) {
      r = r.replace(/\{group_ref\}/g, st.groupRefs[q.repeat_group] || "their");
      const it = st.repeatIter[q.repeat_group] || 1;
      r = r.replace(/\{item_number\}/g, String(it));
      if (q.repeat_group_label) r = r.replace(/\{item_label\}/g, `${q.repeat_group_label} ${it}`);
    }
    return r;
  };
  const botMsg = (text, options = []) => { setMessages(p => [...p, { type: "bot", text }]); setOpts(options); };
  const userMsg = (text) => { setMessages(p => [...p, { type: "user", text }]); setOpts([]); };

  const askQ = () => {
    const q = st.questions[st.qIdx];
    let prefix = "";
    if (q.repeat_group && q.repeat_group_label && !st.groupRefs[q.repeat_group]) {
      const it = st.repeatIter[q.repeat_group] || 1;
      prefix = it === 1 ? `Let's collect info for ${q.repeat_group_label} ${it}.\n` : `Now let's get info for ${q.repeat_group_label} ${it}. `;
    }
    botMsg(prefix + interp(q.text, q), q.input_type === "options" ? q.options : []);
  };
  const advanceQ = () => {
    const cur = st.questions[st.qIdx]; let next = st.qIdx + 1;
    if (cur.repeat_group) {
      const nq = next < st.questions.length ? st.questions[next] : null;
      if (!nq || nq.repeat_group !== cur.repeat_group) {
        const cnt = st.repeatCounts[cur.repeat_group] || 0, it = st.repeatIter[cur.repeat_group] || 1;
        if (it < cnt) { st.repeatIter[cur.repeat_group] = it + 1; st.groupRefs[cur.repeat_group] = null; next = st.questions.findIndex(q => q.repeat_group === cur.repeat_group); }
      }
    }
    const vi = findNext(next);
    if (vi === -1) { botMsg("Preview complete! All questions asked."); setDone(true); } else { st.qIdx = vi; askQ(); }
  };
  const restart = () => {
    setMessages([]); setOpts([]); setDone(false); setInputValue("");
    Object.assign(st, { questions: [...(category.questions || [])].sort((a, b) => a.order - b.order), qIdx: 0, answers: {}, repeatCounts: {}, repeatIter: {}, groupRefs: {} });
    setTimeout(() => {
      const vi = findNext(0);
      if (vi === -1) { botMsg("No questions configured."); setDone(true); return; }
      st.qIdx = vi;
      const fq = st.questions[vi], qt = interp(fq.text, fq);
      botMsg(category.intro_message ? `${category.intro_message}\n${qt}` : qt, fq.input_type === "options" ? fq.options : []);
    }, 50);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!didInit.current) { didInit.current = true; restart(); } }, []);

  const go = (val, label) => {
    userMsg(label || val);
    const q = st.questions[st.qIdx], key = q.repeat_group ? `${q.id}_${st.repeatIter[q.repeat_group] || 1}` : q.id;
    st.answers[key] = val;
    if (q.sets_repeat_for) { st.repeatCounts[q.sets_repeat_for] = parseInt(val) || 1; st.repeatIter[q.sets_repeat_for] = 1; }
    if (q.repeat_group && q.repeat_group_label) st.groupRefs[q.repeat_group] = val;
    advanceQ();
  };
  const handleSubmit = (e) => { e.preventDefault(); const v = inputValue.trim(); if (!v || done) return; setInputValue(""); go(v); };

  return (
    <div data-testid="chatbot-preview-panel" className="fixed bottom-4 right-4 z-50 w-[360px] rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col bg-white animate-fade-up" style={{ maxHeight: "min(530px, calc(100vh - 100px))" }}>
      <div className="bg-[#002147] text-white px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center"><Eye className="h-4 w-4 text-[#D4AF37]" /></div>
          <div><p className="text-xs font-bold tracking-wide">LIVE PREVIEW</p><p className="text-[10px] text-slate-300">{category.name} Flow</p></div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="preview-restart-btn" onClick={restart} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-300 hover:text-white transition-colors" title="Restart"><RotateCcw className="h-3.5 w-3.5" /></button>
          <button data-testid="preview-close-btn" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-300 hover:text-white transition-colors"><X className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50" style={{ minHeight: 0 }}>
        {messages.map((msg, i) => msg.type === "bot" ? (
          <div key={i} className="flex justify-start"><div className="max-w-[85%] px-3 py-2.5 bg-white border border-slate-100 rounded-xl rounded-tl-none shadow-sm">
            <p className="text-xs leading-relaxed text-slate-700 whitespace-pre-line">{msg.text}</p>
            {i === messages.length - 1 && opts.length > 0 && <div className="mt-2 flex flex-col gap-1.5">{opts.map(opt => (<button key={opt.value} onClick={() => go(opt.value, opt.label)} className="w-full text-left px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all">{opt.label}</button>))}</div>}
          </div></div>
        ) : (<div key={i} className="flex justify-end"><div className="max-w-[85%] px-3 py-2 bg-blue-600 text-white rounded-xl rounded-tr-none shadow-sm"><p className="text-xs whitespace-pre-line">{msg.text}</p></div></div>))}
        <div ref={endRef} />
      </div>
      <div className="border-t px-3 py-2.5 bg-white shrink-0">
        {done ? <div className="text-center py-1"><p className="text-[10px] text-emerald-600 font-semibold mb-1">Preview Complete</p><button onClick={restart} className="text-[10px] text-blue-600 font-semibold hover:underline">Restart Preview</button></div>
        : <form onSubmit={handleSubmit} className="flex gap-1.5"><input data-testid="preview-text-input" type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Type answer..." className="flex-1 rounded-xl px-3 py-2 text-xs bg-slate-100 outline-none focus:ring-1 focus:ring-blue-500" /><button data-testid="preview-send-btn" type="submit" disabled={!inputValue.trim()} className="bg-[#002147] text-white p-2 rounded-xl disabled:opacity-40 shrink-0 hover:bg-[#001a3a] transition-colors"><Send className="h-3 w-3" /></button></form>}
      </div>
    </div>
  );
}


// ===================== QUESTION ROW =====================

function QuestionRow({ question, index, total, expanded, onToggle, onUpdate, onMove, onDelete }) {
  const q = question;
  const set = (field, value) => onUpdate({ ...q, [field]: value });
  const setConditionField = (field, value) => {
    const cond = q.condition || { question_id: "", match_values: [], case_insensitive: true };
    onUpdate({ ...q, condition: { ...cond, [field]: value } });
  };
  const addOption = () => onUpdate({ ...q, options: [...(q.options || []), { label: "", value: "" }] });
  const updateOption = (idx, field, val) => { const opts = [...q.options]; opts[idx] = { ...opts[idx], [field]: val }; onUpdate({ ...q, options: opts }); };
  const removeOption = (idx) => onUpdate({ ...q, options: q.options.filter((_, i) => i !== idx) });

  return (
    <div data-testid={`question-row-${index}`} className={`border rounded-lg overflow-hidden transition-all ${expanded ? "border-[#002147] ring-1 ring-[#002147]/20" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors" onClick={onToggle} data-testid={`question-toggle-${index}`}>
        <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
        <span className="text-xs font-mono text-slate-400 w-6 shrink-0">{index + 1}.</span>
        <span className="flex-1 text-sm truncate text-slate-700">{q.text}</span>
        <Badge variant="outline" className="text-[10px] capitalize shrink-0">{q.input_type}</Badge>
        {q.repeat_group && <Badge className="text-[10px] bg-purple-100 text-purple-700 border-0 shrink-0">{q.repeat_group}</Badge>}
        {q.condition && <Badge className="text-[10px] bg-amber-100 text-amber-700 border-0 shrink-0">Cond</Badge>}
        {q.sets_repeat_for && <Badge className="text-[10px] bg-teal-100 text-teal-700 border-0 shrink-0">Repeat</Badge>}
        <div className="flex gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
          <button disabled={index === 0} onClick={() => onMove(-1)} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 transition-colors"><ChevronUp className="h-3.5 w-3.5 text-slate-500" /></button>
          <button disabled={index === total - 1} onClick={() => onMove(1)} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 transition-colors"><ChevronDown className="h-3.5 w-3.5 text-slate-500" /></button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 transition-colors"><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
        </div>
        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform shrink-0 ${expanded ? "rotate-90" : ""}`} />
      </div>
      {expanded && (
        <div className="px-4 py-4 border-t bg-slate-50/80 space-y-4" data-testid={`question-editor-${index}`}>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Question Text</Label>
            <Textarea data-testid={`question-text-${index}`} value={q.text} onChange={e => set("text", e.target.value)} rows={2} className="mt-1 text-sm" placeholder="Enter the question text..." />
            <p className="text-[10px] text-slate-400 mt-1">Use {"{group_ref}"} to reference the name from the first question in a repeat group.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Input Type</Label>
              <select data-testid={`question-type-${index}`} value={q.input_type} onChange={e => set("input_type", e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="text">Text Input</option>
                <option value="options">Options (Buttons)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Question ID</Label>
              <Input data-testid={`question-id-${index}`} value={q.id} onChange={e => set("id", e.target.value)} className="mt-1 text-sm font-mono" placeholder="unique-id" />
            </div>
          </div>
          {q.input_type === "options" && (
            <div>
              <Label className="text-xs font-semibold text-slate-600">Options</Label>
              <div className="space-y-1.5 mt-1">
                {(q.options || []).map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input placeholder="Display Label" value={opt.label} onChange={e => updateOption(idx, "label", e.target.value)} className="text-sm flex-1" />
                    <Input placeholder="Value" value={opt.value} onChange={e => updateOption(idx, "value", e.target.value)} className="text-sm flex-1" />
                    <button onClick={() => removeOption(idx)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={addOption} className="mt-2 text-xs"><Plus className="h-3 w-3 mr-1" /> Add Option</Button>
            </div>
          )}
          <details className="group">
            <summary className="text-xs font-semibold text-slate-500 cursor-pointer select-none hover:text-slate-700 transition-colors">Advanced: Repeat Group Settings</summary>
            <div className="mt-3 grid grid-cols-3 gap-3 pl-2 border-l-2 border-purple-200">
              <div><Label className="text-[10px] text-slate-500">Group Name</Label><Input value={q.repeat_group || ""} onChange={e => set("repeat_group", e.target.value || null)} className="mt-0.5 text-xs" placeholder="e.g. drivers" /></div>
              <div><Label className="text-[10px] text-slate-500">Group Label</Label><Input value={q.repeat_group_label || ""} onChange={e => set("repeat_group_label", e.target.value || null)} className="mt-0.5 text-xs" placeholder="e.g. Driver" /></div>
              <div><Label className="text-[10px] text-slate-500">Sets Repeat For</Label><Input value={q.sets_repeat_for || ""} onChange={e => set("sets_repeat_for", e.target.value || null)} className="mt-0.5 text-xs" placeholder="e.g. drivers" /></div>
            </div>
          </details>
          <details className="group">
            <summary className="text-xs font-semibold text-slate-500 cursor-pointer select-none hover:text-slate-700 transition-colors">Advanced: Conditional Display</summary>
            <div className="mt-3 grid grid-cols-2 gap-3 pl-2 border-l-2 border-amber-200">
              <div><Label className="text-[10px] text-slate-500">Show when Question ID</Label><Input value={q.condition?.question_id || ""} onChange={e => { if (!e.target.value) { onUpdate({ ...q, condition: null }); return; } setConditionField("question_id", e.target.value); }} className="mt-0.5 text-xs font-mono" placeholder="e.g. h-fork" /></div>
              <div><Label className="text-[10px] text-slate-500">Matches values (comma-separated)</Label><Input value={q.condition?.match_values?.join(", ") || ""} onChange={e => setConditionField("match_values", e.target.value.split(",").map(v => v.trim()).filter(Boolean))} className="mt-0.5 text-xs" placeholder="e.g. Medicare, General" /></div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}


// ===================== MAIN DASHBOARD =====================

export default function AdminDashboardPage({ user: initialUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("leads");
  const [user, setUser] = useState(initialUser || location.state?.user || null);

  // --- Chatbot state ---
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [expandedQ, setExpandedQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", value: "", intro_message: "" });
  const [toast, setToast] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  // Fetch user if not passed
  useEffect(() => {
    if (!user) {
      axios.get(`${API}/auth/me`, { withCredentials: true })
        .then(r => setUser(r.data))
        .catch(() => navigate("/login", { replace: true }));
    }
  }, [user, navigate]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/chatbot/categories`, { withCredentials: true });
      setCategories(res.data.categories || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const selectCategory = (cat) => {
    if (dirty && !window.confirm("You have unsaved changes. Discard?")) return;
    setSelectedId(cat.id); setEditData(JSON.parse(JSON.stringify(cat))); setDirty(false); setExpandedQ(null);
  };
  const updateField = (field, value) => { setEditData(prev => ({ ...prev, [field]: value })); setDirty(true); };

  const saveCategory = async () => {
    if (!editData) return; setSaving(true);
    try {
      await axios.put(`${API}/admin/chatbot/categories/${selectedId}`, {
        name: editData.name, value: editData.value, enabled: editData.enabled, order: editData.order,
        intro_message: editData.intro_message, questions: editData.questions.map((q, i) => ({ ...q, order: i + 1 })),
      }, { withCredentials: true });
      setDirty(false); showToast("Category saved!"); await loadCategories();
    } catch (e) { showToast("Failed to save: " + (e.response?.data?.detail || e.message), "error"); }
    finally { setSaving(false); }
  };

  const deleteCategory = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this entire category?")) return;
    try {
      await axios.delete(`${API}/admin/chatbot/categories/${id}`, { withCredentials: true });
      if (selectedId === id) { setSelectedId(null); setEditData(null); setDirty(false); }
      showToast("Deleted"); await loadCategories();
    } catch (e) { showToast("Failed", "error"); }
  };

  const toggleCategoryEnabled = async (cat, e) => {
    e?.stopPropagation();
    try {
      await axios.put(`${API}/admin/chatbot/categories/${cat.id}`, { enabled: !cat.enabled }, { withCredentials: true });
      await loadCategories();
      if (selectedId === cat.id) setEditData(prev => prev ? { ...prev, enabled: !cat.enabled } : prev);
    } catch { showToast("Failed", "error"); }
  };

  const createCategory = async () => {
    if (!newCat.name.trim()) return;
    try {
      const maxOrder = categories.reduce((m, c) => Math.max(m, c.order || 0), 0);
      const res = await axios.post(`${API}/admin/chatbot/categories`, {
        ...newCat, value: newCat.value || newCat.name, order: maxOrder + 1, questions: [],
      }, { withCredentials: true });
      setNewCatOpen(false); setNewCat({ name: "", value: "", intro_message: "" });
      showToast("Created!"); await loadCategories(); selectCategory(res.data);
    } catch (e) { showToast("Failed", "error"); }
  };

  const duplicateCategory = async (cat, e) => {
    e?.stopPropagation();
    try {
      const maxOrder = categories.reduce((m, c) => Math.max(m, c.order || 0), 0);
      await axios.post(`${API}/admin/chatbot/categories`, {
        name: `${cat.name} (Copy)`, value: `${cat.value}_copy`, enabled: false, order: maxOrder + 1,
        intro_message: cat.intro_message, questions: (cat.questions || []).map(q => ({ ...q, id: `${q.id}-copy` })),
      }, { withCredentials: true });
      showToast("Duplicated!"); await loadCategories();
    } catch { showToast("Failed", "error"); }
  };

  const seedDefaults = async () => {
    if (!window.confirm("DELETE all categories and reset to defaults?")) return;
    try {
      await axios.post(`${API}/admin/chatbot/seed?force=true`, {}, { withCredentials: true });
      setSelectedId(null); setEditData(null); setDirty(false); showToast("Reset!"); await loadCategories();
    } catch { showToast("Failed", "error"); }
  };

  const updateQuestion = (qId, uq) => { setEditData(prev => ({ ...prev, questions: prev.questions.map(q => q.id === qId ? uq : q) })); setDirty(true); };
  const addQuestion = () => {
    const nq = { id: `q-${Date.now()}`, text: "New question", input_type: "text", options: [], order: (editData.questions?.length || 0) + 1, repeat_group: null, repeat_group_label: null, sets_repeat_for: null, condition: null };
    setEditData(prev => ({ ...prev, questions: [...prev.questions, nq] })); setExpandedQ(nq.id); setDirty(true);
  };
  const deleteQuestion = (qId) => { setEditData(prev => ({ ...prev, questions: prev.questions.filter(q => q.id !== qId).map((q, i) => ({ ...q, order: i + 1 })) })); if (expandedQ === qId) setExpandedQ(null); setDirty(true); };
  const moveQuestion = (qId, dir) => {
    setEditData(prev => { const qs = [...prev.questions]; const idx = qs.findIndex(q => q.id === qId); const ni = idx + dir; if (ni < 0 || ni >= qs.length) return prev; [qs[idx], qs[ni]] = [qs[ni], qs[idx]]; return { ...prev, questions: qs.map((q, i) => ({ ...q, order: i + 1 })) }; });
    setDirty(true);
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    navigate("/", { replace: true });
  };

  const sortedQuestions = editData?.questions ? [...editData.questions].sort((a, b) => a.order - b.order) : [];

  const tabs = [
    { id: "leads", label: "Leads", icon: FileText },
    { id: "calendar", label: "Calendar", icon: CalendarDays },
    { id: "blog", label: "Blog", icon: PenLine },
    { id: "chatbot", label: "Chatbot", icon: MessageSquare },
    { id: "images", label: "Images", icon: Image },
    { id: "users", label: "Team", icon: Users },
  ];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center"><div className="w-8 h-8 border-2 border-[#002147] border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-slate-500 text-sm">Loading...</p></div></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="admin-dashboard">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium animate-fade-up ${toast.type === "error" ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>{toast.msg}</div>}

      {/* Header */}
      <div className="bg-[#002147]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button data-testid="admin-back-btn" onClick={() => navigate("/")} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"><ArrowLeft className="h-4 w-4" /></button>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">Admin Dashboard</h1>
                {user && <p className="text-xs text-slate-300">{user.name || user.email}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-white/20" />}
              <Button data-testid="admin-logout-btn" variant="outline" size="sm" onClick={handleLogout} className="bg-transparent border-white/30 text-white hover:bg-white/10 text-xs">
                <LogOut className="h-3.5 w-3.5 mr-1" /> Sign Out
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 mt-4 -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? "bg-slate-50 text-[#002147]"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

        {/* ===== LEADS TAB ===== */}
        {activeTab === "leads" && <AdminLeadsTab />}

        {/* ===== CALENDAR TAB ===== */}
        {activeTab === "calendar" && <AdminCalendarTab />}

        {/* ===== BLOG TAB ===== */}
        {activeTab === "blog" && <AdminBlogTab />}

        {/* ===== CHATBOT TAB ===== */}
        {activeTab === "chatbot" && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left: Category List */}
            <div className="lg:w-80 shrink-0">
              <Card className="sticky top-6">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Categories</CardTitle>
                    <div className="flex gap-1.5">
                      <Button data-testid="admin-seed-btn" variant="outline" size="sm" onClick={seedDefaults} className="text-[10px] h-7 px-2"><RotateCcw className="h-3 w-3" /></Button>
                      <Button data-testid="add-category-btn" size="sm" onClick={() => setNewCatOpen(true)} className="bg-[#002147] hover:bg-[#001a3a] text-xs h-7"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1.5 pt-0">
                  {categories.length === 0 ? (
                    <div className="text-center py-8"><Settings className="h-8 w-8 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-400">No categories yet</p></div>
                  ) : categories.map(cat => (
                    <div key={cat.id} data-testid={`category-item-${cat.value}`} onClick={() => selectCategory(cat)}
                      className={`group p-3 rounded-lg border cursor-pointer transition-all ${selectedId === cat.id ? "border-[#002147] bg-[#002147]/5 shadow-sm" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div onClick={e => toggleCategoryEnabled(cat, e)}><Switch checked={cat.enabled} className="scale-75" /></div>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium truncate ${!cat.enabled ? "text-slate-400" : "text-slate-800"}`}>{cat.name}</p>
                            <p className="text-[10px] text-slate-400">{cat.questions?.length || 0} questions</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                          <button onClick={e => duplicateCategory(cat, e)} className="p-1 rounded hover:bg-slate-200" title="Duplicate"><Copy className="h-3 w-3 text-slate-400" /></button>
                          <button onClick={e => deleteCategory(cat.id, e)} className="p-1 rounded hover:bg-red-50" title="Delete"><Trash2 className="h-3 w-3 text-red-400" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Right: Editor */}
            <div className="flex-1 min-w-0">
              {editData ? (
                <div className="space-y-5">
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2"><Pencil className="h-4 w-4 text-slate-400" /> Category Settings</CardTitle>
                        <div className="flex items-center gap-3">
                          <Button data-testid="live-preview-btn" size="sm" variant="outline" onClick={() => setPreviewOpen(!previewOpen)}
                            className={`text-xs h-8 gap-1.5 ${previewOpen ? "bg-emerald-50 border-emerald-300 text-emerald-700" : ""}`}>
                            <Play className="h-3.5 w-3.5" /> {previewOpen ? "Preview Open" : "Live Preview"}
                          </Button>
                          <div className="flex items-center gap-2">
                            <Switch data-testid="category-enabled-toggle" checked={editData.enabled} onCheckedChange={v => updateField("enabled", v)} />
                            <Label className="text-xs text-slate-500">Enabled</Label>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><Label className="text-xs font-semibold text-slate-600">Display Name</Label><Input data-testid="category-name-input" value={editData.name} onChange={e => updateField("name", e.target.value)} className="mt-1" placeholder="e.g. Auto Insurance" /></div>
                        <div><Label className="text-xs font-semibold text-slate-600">Internal Value</Label><Input data-testid="category-value-input" value={editData.value} onChange={e => updateField("value", e.target.value)} className="mt-1 font-mono text-sm" placeholder="e.g. Auto" /></div>
                      </div>
                      <div><Label className="text-xs font-semibold text-slate-600">Intro Message</Label><Input data-testid="category-intro-input" value={editData.intro_message} onChange={e => updateField("intro_message", e.target.value)} className="mt-1" placeholder="Shown before the first question" /></div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Questions ({sortedQuestions.length})</CardTitle>
                        <Button data-testid="add-question-btn" size="sm" onClick={addQuestion} className="bg-[#002147] hover:bg-[#001a3a] text-xs h-8"><Plus className="h-3.5 w-3.5 mr-1" /> Add Question</Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {sortedQuestions.length === 0
                        ? <div className="text-center py-10 text-slate-400"><p className="text-sm">No questions yet.</p></div>
                        : <div className="space-y-1.5">{sortedQuestions.map((q, i) => (
                            <QuestionRow key={q.id} question={q} index={i} total={sortedQuestions.length} expanded={expandedQ === q.id}
                              onToggle={() => setExpandedQ(expandedQ === q.id ? null : q.id)} onUpdate={uq => updateQuestion(q.id, uq)}
                              onMove={dir => moveQuestion(q.id, dir)} onDelete={() => deleteQuestion(q.id)} />
                          ))}</div>
                      }
                    </CardContent>
                  </Card>

                  {dirty && (
                    <div className="sticky bottom-4 flex justify-end">
                      <Button data-testid="save-category-btn" onClick={saveCategory} disabled={saving} className="bg-[#002147] hover:bg-[#001a3a] shadow-lg px-6">
                        <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-80"><div className="text-center"><Settings className="h-12 w-12 mx-auto mb-3 text-slate-200" /><p className="text-lg font-medium text-slate-400">Select a category to edit</p><p className="text-sm text-slate-300 mt-1">Choose from the left, or add a new one</p></div></div>
              )}
            </div>
          </div>
        )}

        {/* ===== IMAGES TAB ===== */}
        {activeTab === "images" && <AdminImagesTab />}

        {/* ===== USERS TAB ===== */}
        {activeTab === "users" && <AdminUsersTab currentUser={user} />}
      </div>

      {/* New Category Dialog */}
      <Dialog open={newCatOpen} onOpenChange={setNewCatOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-category-dialog">
          <DialogHeader><DialogTitle>Create New Category</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs font-semibold">Category Name</Label><Input data-testid="new-cat-name" value={newCat.name} onChange={e => setNewCat(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Annuities" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Internal Value</Label><Input data-testid="new-cat-value" value={newCat.value} onChange={e => setNewCat(p => ({ ...p, value: e.target.value }))} placeholder="e.g. Annuities" className="mt-1 font-mono" /></div>
            <div><Label className="text-xs font-semibold">Intro Message</Label><Input data-testid="new-cat-intro" value={newCat.intro_message} onChange={e => setNewCat(p => ({ ...p, intro_message: e.target.value }))} placeholder="e.g. Let's explore annuity options..." className="mt-1" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" size="sm">Cancel</Button></DialogClose>
            <Button data-testid="create-category-submit" size="sm" onClick={createCategory} className="bg-[#002147] hover:bg-[#001a3a]">Create Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live Preview */}
      {previewOpen && editData && <ChatbotPreview key={editData.id} category={editData} onClose={() => setPreviewOpen(false)} />}
    </div>
  );
}
